import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { format, isSameDay, addDays, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Radio, BookOpen, CalendarRange, ListTodo, CalendarDays, PieChart, Users, Clock, DollarSign, Video, ArrowUpRight,
} from 'lucide-react';
import { useAuth } from '../../hooks/AuthContext';
import { db } from '../../services/firebase';
import { setCalendarWorkspace, getCalendarWorkspaceId, getInstructors } from '../../modules/calendar/services';
import { subscribeToMyTasks } from '../../modules/taskManager/services/firestore';

// ============================================================================
// Inicio del Gestor: accesos rápidos + resumen del día (masterclasses, clases,
// semana y mis tareas). Reemplaza a /panel (Gestor Pro) como pantalla de entrada.
// ============================================================================

const isMasterclass = (ev) => {
  const type = String(ev.type || ev.tipo || '').toLowerCase();
  if (type.includes('masterclass') || type === 'mc') return true;
  return /\bMC\b/.test(String(ev.title || ''));
};
const isCancelled = (ev) => ev.status === 'cancelled' || ev.status === 'cancelado';
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

const QUICK_ACTIONS = [
  { label: 'Abrir calendario', desc: 'Masterclasses y clases del mes', icon: CalendarDays, href: '/calendar', perm: 'calendario', tone: 't-blue' },
  { label: 'Planificación', desc: 'Programar grupos e instructores', icon: PieChart, href: '/planning', perm: 'calendario', tone: 't-sky' },
  { label: 'Mis tareas', desc: 'Lo que tienes asignado', icon: ListTodo, href: '/tareas/mis-tareas', perm: 'tareas', tone: 't-green' },
  { label: 'Cursos', desc: 'Cursos activos e instructores', icon: BookOpen, href: '/courses', perm: 'calendario', tone: 't-amber' },
  { label: 'Instructores', desc: 'Carga y disponibilidad', icon: Users, href: '/instructors', perm: 'calendario', tone: 't-blue' },
  { label: 'Marcar asistencia', desc: 'Entrada, salida y horas extra', icon: Clock, href: '/horas-extras', perm: 'horasExtra', tone: 't-red' },
  { label: 'Ventas', desc: 'Asesores y comisiones', icon: DollarSign, href: '/sales-advisor', perm: 'ventas', tone: 't-green', seller: true },
  { label: 'Cuentas Zoom', desc: 'Disponibilidad de salas', icon: Video, href: '/zoom-accounts', perm: 'calendario', tone: 't-sky' },
];

const greeting = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

const HomeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [now] = useState(() => new Date());
  const [events, setEvents] = useState(null); // null = cargando
  const [instructors, setInstructors] = useState({});
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState('all');

  const isAdmin = user?.role === 'admin';
  const perms = user?.appPermissions || {};
  const allowed = (perm) => isAdmin || perms[perm] !== false;
  const canCalendar = allowed('calendario');
  const canTasks = allowed('tareas');

  // Eventos de hoy y los próximos 6 días (una sola lectura por rango de fecha;
  // el workspace y los ocultos se filtran en el cliente para no requerir índice).
  useEffect(() => {
    if (!user?.uid || !canCalendar) { setEvents([]); return undefined; }
    let alive = true;
    (async () => {
      try {
        await setCalendarWorkspace(user.uid);
        const ws = getCalendarWorkspaceId();
        const from = startOfDay(now);
        const to = addDays(from, 7);
        const snap = await getDocs(query(
          collection(db, 'schedules'),
          where('start', '>=', Timestamp.fromDate(from)),
          where('start', '<', Timestamp.fromDate(to)),
        ));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((ev) => ev.hidden !== true && (!ws || !ev.workspaceId || ev.workspaceId === ws))
          .map((ev) => ({ ...ev, start: toDate(ev.start), end: toDate(ev.end) }))
          .sort((a, b) => a.start - b.start);
        const inst = await getInstructors().catch(() => []);
        if (!alive) return;
        setInstructors(Object.fromEntries(inst.map((i) => [i.id, i.name])));
        setEvents(list);
      } catch (e) {
        console.error('[Inicio] eventos:', e);
        if (alive) setEvents([]);
      }
    })();
    return () => { alive = false; };
  }, [user?.uid, canCalendar, now]);

  useEffect(() => {
    if (!user?.email || !canTasks) { setTasks([]); return undefined; }
    return subscribeToMyTasks(user.email, user.workspaceId, (list) => setTasks(list));
  }, [user?.email, user?.workspaceId, canTasks]);

  const today = useMemo(() => (events || []).filter((ev) => ev.start && isSameDay(ev.start, now)), [events, now]);
  const todayActive = today.filter((ev) => !isCancelled(ev));
  const mcToday = todayActive.filter(isMasterclass);
  const clToday = todayActive.filter((ev) => !isMasterclass(ev));
  const cancelledToday = today.filter(isCancelled).length;
  const nextMc = mcToday.find((ev) => ev.start > now);
  const nextCl = clToday.find((ev) => ev.start > now);

  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const day = addDays(startOfDay(now), i);
    const evs = (events || []).filter((ev) => ev.start && isSameDay(ev.start, day) && !isCancelled(ev));
    return { day, mc: evs.filter(isMasterclass).length, cl: evs.filter((e) => !isMasterclass(e)).length };
  }), [events, now]);
  const weekMc = week.reduce((s, d) => s + d.mc, 0);
  const weekCl = week.reduce((s, d) => s + d.cl, 0);

  const pending = useMemo(() => (tasks || []).filter((t) => t.estado !== 'finalizado'), [tasks]);
  const dueSoon = useMemo(() => {
    const endToday = addDays(startOfDay(now), 1);
    return pending
      .map((t) => ({ ...t, due: t.fechaVencimiento ? new Date(t.fechaVencimiento) : null }))
      .filter((t) => t.due && !Number.isNaN(t.due.getTime()) && t.due < endToday)
      .sort((a, b) => a.due - b.due);
  }, [pending, now]);
  const overdue = dueSoon.filter((t) => t.due < startOfDay(now)).length;

  const agenda = today.filter((ev) => {
    if (filter === 'mc') return isMasterclass(ev);
    if (filter === 'cl') return !isMasterclass(ev);
    return true;
  });

  const actions = QUICK_ACTIONS.filter((a) => allowed(a.perm) && (!a.seller || isAdmin || user?.isSeller));
  const firstName = (user?.displayName || user?.email || '').split(/[ @]/)[0];
  const loadingEvents = events === null;

  const pill = (ev) => {
    if (isCancelled(ev)) return <span className="pill t-red">Cancelada</span>;
    return isMasterclass(ev)
      ? <span className="pill t-green">Masterclass</span>
      : <span className="pill t-blue">Clase</span>;
  };

  return (
    <div className="hx-home">
      <div className="crumb">Hingenia › Inicio</div>
      <h1>{greeting(now)}{firstName ? `, ${firstName}` : ''}</h1>
      <p className="lead" style={{ textTransform: 'capitalize' }}>{format(now, "EEEE d 'de' MMMM, yyyy", { locale: es })}</p>

      {canCalendar && (
        <>
          <div className="h-sec">Resumen de hoy</div>
          <div className="grid g4">
            <div className="card kpi">
              <div className="lbl"><span className="ico t-green"><Radio size={15} /></span>Masterclasses hoy</div>
              <div className="num">{loadingEvents ? '–' : mcToday.length}</div>
              <div className="hint">{nextMc ? `Próxima ${format(nextMc.start, 'h:mm a')}` : mcToday.length ? 'Ya no quedan más hoy' : 'Sin masterclasses hoy'}</div>
            </div>
            <div className="card kpi">
              <div className="lbl"><span className="ico t-blue"><BookOpen size={15} /></span>Clases hoy</div>
              <div className="num">{loadingEvents ? '–' : clToday.length}</div>
              <div className="hint">{nextCl ? `Próxima ${format(nextCl.start, 'h:mm a')}` : cancelledToday ? `${cancelledToday} cancelada${cancelledToday > 1 ? 's' : ''}` : 'Sin clases pendientes'}</div>
            </div>
            <div className="card kpi">
              <div className="lbl"><span className="ico t-sky"><CalendarRange size={15} /></span>Próximos 7 días</div>
              <div className="num">{loadingEvents ? '–' : weekMc + weekCl}</div>
              <div className="hint">{weekMc} masterclasses · {weekCl} clases</div>
            </div>
            {canTasks && (
              <div className="card kpi">
                <div className="lbl"><span className="ico t-amber"><ListTodo size={15} /></span>Mis tareas pendientes</div>
                <div className="num">{tasks === null ? '–' : pending.length}</div>
                <div className="hint">{overdue ? <span style={{ color: 'var(--bad)', fontWeight: 600 }}>{overdue} vencida{overdue > 1 ? 's' : ''}</span> : 'Nada vencido'} · {dueSoon.length - overdue} para hoy</div>
              </div>
            )}
          </div>
        </>
      )}

      {actions.length > 0 && (
        <>
          <div className="h-sec">Accesos rápidos</div>
          <div className="grid g-actions">
            {actions.map((a) => (
              <button key={a.href + a.label} type="button" className="card action" onClick={() => navigate(a.href)}>
                <span className={`ico ${a.tone}`}><a.icon size={18} /></span>
                <span>
                  <b>{a.label}</b>
                  <small>{a.desc}</small>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="grid g-main" style={{ marginTop: 28 }}>
        {canCalendar && (
          <div>
            <div className="h-sec" style={{ marginTop: 0 }}>
              Agenda de hoy <small>{today.length} evento{today.length === 1 ? '' : 's'}</small>
              <div className="tabs">
                {[['all', 'Todo'], ['mc', 'Masterclass'], ['cl', 'Clases']].map(([k, l]) => (
                  <button key={k} type="button" className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="card list">
              {loadingEvents ? (
                <><div className="skel" /><div className="skel" /><div className="skel" /></>
              ) : agenda.length === 0 ? (
                <div className="empty">No hay {filter === 'mc' ? 'masterclasses' : filter === 'cl' ? 'clases' : 'eventos'} programados para hoy.</div>
              ) : (
                agenda.map((ev) => (
                  <div key={ev.id} className={`row${isCancelled(ev) ? ' done' : ''}`} role="button" tabIndex={0}
                    onClick={() => navigate('/calendar')} onKeyDown={(e) => e.key === 'Enter' && navigate('/calendar')} style={{ cursor: 'pointer' }}>
                    <span className="time">{format(ev.start, 'h:mm a')}</span>
                    <div className="main">
                      <b>{ev.title || 'Sin título'}</b>
                      <small>{instructors[ev.instructorId] || 'Sin instructor'}{ev.end ? ` · hasta ${format(ev.end, 'h:mm a')}` : ''}</small>
                    </div>
                    {pill(ev)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div>
          {canCalendar && (
            <>
              <div className="h-sec" style={{ marginTop: 0 }}>
                Esta semana
                <button type="button" className="lnk" onClick={() => navigate('/calendar')}>Ver calendario <ArrowUpRight size={13} style={{ verticalAlign: '-2px' }} /></button>
              </div>
              <div className="card week">
                {week.map((d, i) => (
                  <div key={d.day.toISOString()} className={`day${i === 0 ? ' today' : ''}`}>
                    <div className="dn">{format(d.day, 'EEE', { locale: es })}</div>
                    <div className="dd">{format(d.day, 'd')}</div>
                    <div className="c"><em>{d.mc}</em> MC</div>
                    <div className="c"><em>{d.cl}</em> CL</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {canTasks && (
            <>
              <div className="h-sec">
                Mis tareas para hoy
                <button type="button" className="lnk" onClick={() => navigate('/tareas/mis-tareas')}>Ver todas <ArrowUpRight size={13} style={{ verticalAlign: '-2px' }} /></button>
              </div>
              <div className="card list">
                {tasks === null ? (
                  <><div className="skel" /><div className="skel" /></>
                ) : dueSoon.length === 0 ? (
                  <div className="empty">No tienes tareas vencidas ni para hoy.</div>
                ) : (
                  dueSoon.slice(0, 8).map((t) => {
                    const late = t.due < startOfDay(now);
                    return (
                      <div key={t.id} className="row" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                        onClick={() => navigate('/tareas/mis-tareas')} onKeyDown={(e) => e.key === 'Enter' && navigate('/tareas/mis-tareas')}>
                        <div className="main">
                          <b>{t.titulo || 'Sin título'}</b>
                          <small>{late ? `Venció el ${format(t.due, "d 'de' MMM", { locale: es })}` : 'Vence hoy'}</small>
                        </div>
                        <span className={`pill ${late ? 't-red' : 't-amber'}`}>{late ? 'Vencida' : 'Hoy'}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
